import {
    createShortcut,
    deleteShortcut,
    getShortcuts,
    reorderShortcuts,
    updateShortcut,
    type TaskShortcut,
} from "@dashboard/api/task";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const SHORTCUTS_QUERY_KEY = "shortcuts";

export function useShortcuts(instanceId?: number) {
    return useQuery({
        queryKey: [SHORTCUTS_QUERY_KEY, instanceId],
        queryFn: () => getShortcuts({ instanceId }),
        enabled: instanceId !== undefined,
    });
}

export function useCreateShortcut() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createShortcut,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [SHORTCUTS_QUERY_KEY] });
        },
    });
}

export function useUpdateShortcut() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: Parameters<typeof updateShortcut>[1] }) =>
            updateShortcut(id, data),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [SHORTCUTS_QUERY_KEY] });
        },
    });
}

export function useDeleteShortcut() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteShortcut,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [SHORTCUTS_QUERY_KEY] });
        },
    });
}

export function useReorderShortcuts(instanceId?: number) {
    const queryClient = useQueryClient();
    const queryKey = [SHORTCUTS_QUERY_KEY, instanceId];

    return useMutation({
        mutationFn: (reordered: TaskShortcut[]) =>
            reorderShortcuts(reordered.map((s) => ({ id: s.id, order: s.order }))),
        onMutate: async (reordered) => {
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData<TaskShortcut[]>(queryKey);
            queryClient.setQueryData<TaskShortcut[]>(queryKey, reordered);
            return { previous };
        },
        onError: (_err, _vars, context) => {
            if (context?.previous) {
                queryClient.setQueryData(queryKey, context.previous);
            }
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: [SHORTCUTS_QUERY_KEY] });
        },
    });
}
